import { useState, useCallback, useRef, useEffect } from 'react'
import { useParams } from 'react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useUploadSession } from '../hooks/use-session'
import { useUploadTranslations } from '../hooks/use-upload-translations'
import { IrisShell } from '../components/iris-shell'
import { SessionGate } from '../components/session-gate'
import { PhotoTipsBanner } from '../components/photo-tips-banner'
import { SlotProgress } from '../components/slot-progress'
import { SlotCard } from '../components/slot-card'
import { FinaliseSection } from '../components/finalise-section'
import { SuccessScreen } from '../components/success-screen'
import { ProgressSavedScreen } from '../components/progress-saved-screen'
import { DisclaimerAck } from '@/shared/components/claims/disclaimer-ack'
import type { UploadSession, Shortfall } from '../types'

function ActiveSession({
  session,
  token,
  isDemo,
  onFinalised,
}: {
  session: UploadSession
  token: string
  isDemo?: boolean
  onFinalised: () => void
}) {
  const [savedProgressEmail, setSavedProgressEmail] = useState<string | null>(null)
  const [slotErrors, setSlotErrors] = useState<Record<string, string>>({})
  // Disclaimer acknowledgement is lifted here so the notice + confirmation can
  // render at the TOP of the capture (before the customer selects any photos)
  // while still gating the finalise submit down in FinaliseSection (DL-46).
  const [acknowledged, setAcknowledged] = useState(false)
  // Set when the user presses Submit with a required acknowledgement unticked;
  // paints the error border/message on the top-of-page disclaimer.
  const [disclaimerError, setDisclaimerError] = useState(false)
  const slotRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const handleAcknowledgedChange = useCallback((next: boolean) => {
    setAcknowledged(next)
    if (next) setDisclaimerError(false)
  }, [])

  const handleDisclaimerViolation = useCallback(() => {
    setDisclaimerError(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const handleShortfalls = useCallback((shortfalls: Shortfall[]) => {
    const errors: Record<string, string> = {}
    shortfalls.forEach((s) => {
      errors[s.slot_uuid] = `${s.need - s.have} more required`
    })
    setSlotErrors(errors)

    const firstKey = shortfalls[0]?.slot_uuid
    if (firstKey && slotRefs.current[firstKey]) {
      slotRefs.current[firstKey]?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [])

  const handleSuccess = useCallback(() => {
    onFinalised()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [onFinalised])

  const handleSaveProgress = useCallback((email: string) => {
    setSavedProgressEmail(email)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  if (savedProgressEmail !== null) {
    return (
      <ProgressSavedScreen
        email={savedProgressEmail}
        onResume={() => setSavedProgressEmail(null)}
      />
    )
  }

  const sortedSlots = [...session.slots].sort((a, b) => a.ordering - b.ordering)

  return (
    <>
      {/* Title */}
      <div className="panel-arrive mb-6">
        <h1
          className="mb-2 font-display text-[26px] font-light leading-tight text-[var(--ink)]"
        >
          {session.presentation.title}
        </h1>
        {session.presentation.intro_html && (
          <div
            className="clara-body text-[var(--ink-2)]"
            dangerouslySetInnerHTML={{ __html: session.presentation.intro_html }}
          />
        )}
      </div>

      {/* Disclaimer + confirmation - shown at the top so the customer
          acknowledges before selecting any photos. Gates the finalise submit
          below via the shared `acknowledged` state. */}
      {session.disclaimer && (
        <DisclaimerAck
          className="mb-6"
          disclaimer={session.disclaimer}
          acknowledged={acknowledged}
          onAcknowledgedChange={handleAcknowledgedChange}
          invalid={disclaimerError}
        />
      )}

      {/* Photo tips */}
      <PhotoTipsBanner tips={session.presentation.photo_tips || []} />

      {/* Progress */}
      <SlotProgress slots={session.slots} />

      {/* Slot cards */}
      <div className="space-y-4">
        {sortedSlots.map((slot, i) => (
          <div key={slot.id} ref={(el) => { slotRefs.current[slot.id] = el }}>
            <SlotCard
              slot={slot}
              token={token}
              index={i}
              error={slotErrors[slot.id]}
              isDemo={isDemo}
            />
          </div>
        ))}
      </div>

      {/* Finalise */}
      <FinaliseSection
        token={token}
        slots={session.slots}
        disclaimer={session.disclaimer}
        acknowledged={acknowledged}
        onDisclaimerViolation={handleDisclaimerViolation}
        onShortfalls={handleShortfalls}
        onSuccess={handleSuccess}
        onSaveProgress={handleSaveProgress}
        isDemo={isDemo}
      />

      {/* Outro (shown before submit too, if provided) */}
      {session.presentation.outro_html && (
        <div
          className="clara-body mt-8 text-center text-[var(--ink-3)]"
          dangerouslySetInnerHTML={{ __html: session.presentation.outro_html }}
        />
      )}
    </>
  )
}

export default function UploadPage() {
  const { token } = useParams<{ token: string }>()
  const isDemo = import.meta.env.DEV && token === 'demo'
  const queryClient = useQueryClient()
  const [demoSeeded, setDemoSeeded] = useState(false)
  // Held above the gate: the post-finalise refetch returns the session as
  // finalised, which the gate reads as a later revisit ("already submitted").
  const [finalisedThisVisit, setFinalisedThisVisit] = useState(false)
  const handleFinalised = useCallback(() => setFinalisedThisVisit(true), [])

  useEffect(() => {
    if (!isDemo) return
    import('../lib/mock-session').then((m) => {
      queryClient.setQueryData(['upload-session', 'demo'], m.MOCK_SESSION)
      setDemoSeeded(true)
    })
  }, [isDemo, queryClient])

  const { data: session, isLoading, isError } = useUploadSession(
    isDemo ? (demoSeeded ? 'demo' : '') : (token || ''),
  )

  const effectiveSession = isDemo ? (session ?? queryClient.getQueryData<UploadSession>(['upload-session', 'demo'])) : session
  // Overlay the tenant's copy overrides once the session resolves its slug.
  const i18nReady = useUploadTranslations(effectiveSession?.tenant_slug)
  const effectiveLoading =
    (isDemo ? !demoSeeded : isLoading) ||
    (!!effectiveSession?.tenant_slug && !i18nReady)
  const effectiveError = isDemo ? false : isError

  return (
    <IrisShell>
      {finalisedThisVisit ? (
        <SuccessScreen outroHtml={effectiveSession?.presentation.outro_html} />
      ) : (
        <SessionGate session={effectiveSession} isLoading={effectiveLoading} isError={effectiveError}>
          {(activeSession) => (
            <ActiveSession
              session={activeSession}
              token={token!}
              isDemo={isDemo}
              onFinalised={handleFinalised}
            />
          )}
        </SessionGate>
      )}
    </IrisShell>
  )
}
